'use client';

import { useQuery } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Trans } from '@kit/ui/trans';

import { SectionHeader } from '~/(tenant)/_components/native/_primitives';

// Cross-segment import: the (tenant) layout group is shared, and other native
// sections (e.g. native-home-renderer's coaches/today-classes imports) follow
// the same pattern. NativeMembershipCard owns the editorial card design we
// want here; extracting it would just leave a thin re-export.
import { NativeMembershipCard } from '~/(tenant)/memberships/_components/native-membership-card';
// `import type` from a `'server-only'` module is safe — TypeScript erases
// type-only imports before bundling, so the server-only guard never runs.
import type { MyMembershipRow } from '~/(tenant)/memberships/_lib/server/memberships-page.loader';

interface MyMembershipsSectionProps {
  accountId: string;
}

/**
 * Glanceable equivalent of the /memberships Active tab.
 *
 * Hidden when:
 *   - the user is signed out
 *   - the user has no membership at this tenant
 *   - the only membership is cancelled (matches the member-id-card rule)
 *
 * Data fetch mirrors `loadMyMembershipsForTenant` on the server — same RPC,
 * same shape. The RPC authorizes self and tenant-linked children from the
 * authenticated subject before returning its safe projection.
 */
export function MyMembershipsSection({ accountId }: MyMembershipsSectionProps) {
  const client = useSupabase();

  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
    staleTime: 60_000,
  });

  const userId = session?.user?.id;

  const { data: memberships } = useQuery({
    queryKey: ['native-home-my-memberships', accountId, userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async (): Promise<MyMembershipRow[]> => {
      // Same cast pattern the server loader uses — `get_my_memberships` lands
      // in generated types after the next typegen run. `.call(client, ...)`
      // preserves `this` because we aliased `client.rpc` off the instance.
      const rpc = client.rpc as unknown as (
        name: string,
        args: Record<string, unknown>,
      ) => Promise<{
        data: MyMembershipRow[] | null;
        error: { message: string } | null;
      }>;

      const { data, error } = await rpc.call(client, 'get_my_memberships', {
        p_account_id: accountId,
        p_include_children: true,
      });

      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });

  if (!userId) return null;

  const visible = (memberships ?? []).filter((m) => m.status !== 'cancelled');
  if (visible.length === 0) return null;

  const titleKey =
    visible.length === 1
      ? 'common:nativeHome.myMemberships.title'
      : 'common:nativeHome.myMemberships.titlePlural';
  const titleDefault = visible.length === 1 ? 'Your card' : 'Your cards';

  return (
    <MyMembershipsLayout
      memberships={visible}
      titleKey={titleKey}
      titleDefault={titleDefault}
    />
  );
}

function MyMembershipsLayout({
  memberships,
  titleKey,
  titleDefault,
}: {
  memberships: MyMembershipRow[];
  titleKey: string;
  titleDefault: string;
}) {
  return (
    <section style={{ paddingTop: 32 }} data-test="native-my-memberships">
      <SectionHeader
        eyebrow={
          <Trans
            i18nKey="common:nativeHome.myMemberships.eyebrow"
            defaults="Membership"
          />
        }
        title={<Trans i18nKey={titleKey} defaults={titleDefault} />}
        action={{
          label: (
            <Trans
              i18nKey="common:nativeHome.myMemberships.manage"
              defaults="Manage"
            />
          ),
          href: '/memberships',
        }}
      />

      <div
        style={{
          marginTop: 18,
          paddingInline: 22,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {memberships.map((membership) => (
          <NativeMembershipCard
            key={membership.id}
            membership={membership}
          />
        ))}
      </div>
    </section>
  );
}
